import { useState, useEffect, type FormEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import { ApiError } from '@/api/client'
import { useProfiles } from '@/api/profiles'
import { WAVE_ECONOMICS_CURRENT_VERSION, useWaveDossier, useWaveProposals, useWaveReadiness, useWaves, type WaveDossier, type WaveQueryEconomics } from '@/api/waves'
import Card from '@/components/Card'
import SectionHead from '@/components/SectionHead'
import { RolloutEditor } from './wave-economics/RolloutEditor'
import { DemoWaveEconomics } from './wave-economics/DemoWaveEconomics'
import { safeConfigReason, safeOperatorText } from './wave-economics/operatorText'

// ============================================================
//  Wellen-Economics (Shadow-v1) — Beobachtungsansicht.
//
//  Die Seite zeigt Margen-Cap und optionalen Sellerboard-AOV-Vergleich je
//  Suchterm in Euro je Klick. Das Gebot folgt Marge und CPC-Regel. Dazu zeigt
//  sie das Alter der Daten und die Sperrgruende. Sie schreibt nichts: der
//  Dossier-Aufbau ist ein gegateter Serverschritt, und ein Shadow-v1-Dossier ist
//  serverseitig ohnehin nicht scharfschaltbar.
//
//  Bewusst NICHT dargestellt: Token, Basis-URL, Seller-ID und sonstige interne
//  Kennungen. Sichtbar sind nur die NAMEN fehlender Konfigurationsstellen — sie
//  sagen dem Operator, was zu tun ist, ohne einen Wert preiszugeben.
// ============================================================

/** Deterministische Euro-Ausgabe (keine Locale-Abhaengigkeit im Test). */
export function eur(value: number): string {
  return `${value.toFixed(2).replace('.', ',')} €`
}

function pct(value: number): string {
  const text = (value * 100).toFixed(1).replace('.', ',')
  return `${value > 0 ? '+' : ''}${text} %`
}

/** Ab hier wird hervorgehoben: STRIKT ueber 20 % (genau 20 % nicht). */
export const ABWEICHUNG_SCHWELLE = 0.2

export interface DeviationCell {
  text: string
  highlight: boolean
}

/**
 * Die Vergleichszelle. Sie rechnet aus den beiden Deckeln selbst — ein
 * fehlender oder nullwertiger Vergleichswert ergibt deshalb keinen Prozentwert,
 * sondern einen benannten Grund. „Unendlich viel besser" waere keine Aussage.
 */
export function deviationCell(economics: WaveQueryEconomics | undefined): DeviationCell {
  if (!economics) return { text: 'Kein Vergleich (kein Economics-Block)', highlight: false }
  const stahlia = economics.stahlia_margin_cap
  const legacy = economics.legacy_aov_cap
  if (stahlia === null) return { text: 'Kein Margen-Cap (Marge nicht bestätigt)', highlight: false }
  if (legacy === null) return { text: 'Kein Vergleich (AOV-Cap fehlt)', highlight: false }
  if (legacy === 0) return { text: 'Kein Vergleich (AOV-Cap ist 0,00 €)', highlight: false }
  const abweichung = (stahlia - legacy) / legacy
  return { text: pct(abweichung), highlight: Math.abs(abweichung) > ABWEICHUNG_SCHWELLE }
}

/** Datenalter in Worten — `null` heisst „unbekannt", nicht „frisch". */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return 'unbekannt'
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} Sekunden alt`
  if (seconds < 3600) return `${Math.floor(seconds / 60)} Minuten alt`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} Stunden alt`
  return `${Math.floor(seconds / 86400)} Tage alt`
}

/** Berechnet das dynamische Datenalter bezogen auf jetzt (oder Fallback auf Erstellungsalter). */
export function computeCurrentAgeSeconds(
  checkedAt: string | null | undefined,
  fallbackSeconds: number | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (checkedAt) {
    const parsed = Date.parse(checkedAt)
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.round((nowMs - parsed) / 1000))
    }
  }
  return fallbackSeconds ?? null
}

function parseWaveId(raw: string | undefined): number | null {
  if (raw === undefined || !/^[1-9]\d*$/.test(raw)) return null
  const value = Number(raw)
  return Number.isSafeInteger(value) ? value : null
}

function WaveIdForm({ initial, profileId }: { initial: string; profileId: number | null }) {
  const navigate = useNavigate()
  const [value, setValue] = useState(initial)
  const [hint, setHint] = useState<string | null>(null)
  useEffect(() => setValue(initial), [initial])

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    const id = parseWaveId(value.trim())
    if (id === null) {
      setHint('Bitte eine positive Wellen-Nummer eingeben (z. B. 1).')
      return
    }
    setHint(null)
    navigate(`/wave-economics/${id}${profileId === null ? '' : `?profile=${profileId}`}`)
  }

  return (
    <Card className="p-4">
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="wave-id" className="block text-xs text-[var(--muted-foreground)]">
            Wellen-Nummer
          </label>
          <input
            id="wave-id"
            name="wave-id"
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-40 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm"
          />
        </div>
        <button
          type="submit"
          className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm font-medium"
        >
          Dossier anzeigen
        </button>
      </form>
      {hint && <p className="mt-2 text-xs text-[var(--destructive)]">{hint}</p>}
    </Card>
  )
}

function ReasonList({ title, reasons, config = false }: { title: string; reasons: string[]; config?: boolean }) {
  if (reasons.length === 0) return null
  return (
    <div className="mt-2">
      <p className="text-xs font-semibold text-[var(--foreground)]">{title}</p>
      <ul className="mt-1 list-disc pl-5 text-xs text-[var(--muted-foreground)]">
        {reasons.map((reason, index) => (
          <li key={index}>{config ? safeConfigReason(reason) : safeOperatorText(reason)}</li>
        ))}
      </ul>
    </div>
  )
}

function EconomicsHead({ dossier }: { dossier: WaveDossier }) {
  const economics = dossier.economics
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  if (!economics) {
    // Alt-Dossier: es gibt schlicht keine Economics-Aussage. Das ist ein eigener
    // Zustand — nicht „alles in Ordnung" und nicht „gesperrt".
    return (
      <Card className="p-4" data-testid="legacy-dossier">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
          <div>
            <p className="text-sm font-semibold">Alt-Dossier ohne Economics-Block</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Dieses Dossier stammt aus der Zeit vor Shadow-v1. Es trägt keinen Margen-Cap,
              keine Datenherkunft und keine Gültigkeit. Erst ein neuer Dossier-Aufbau erzeugt
              die Economics-Aussage; bis dahin sind gespeicherte Vorschläge nicht mehr gebunden.
            </p>
          </div>
        </div>
      </Card>
    )
  }

  const provider = economics.sources.provider
  const stale = economics.economics_mode === 'shadow_v1'
    && economics.version !== WAVE_ECONOMICS_CURRENT_VERSION
  return (
    <Card className="p-4" data-testid="economics-head">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md border border-[var(--border)] px-2 py-0.5 text-xs font-semibold">
          Modus: {economics.economics_mode}
        </span>
        <span className="flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-xs font-semibold">
          <ShieldAlert className="h-3.5 w-3.5" />
          Nicht scharfschaltbar
        </span>
        <span className="text-xs text-[var(--muted-foreground)]">
          Ziel-ACoS {(dossier.targetAcos * 100).toFixed(0)} %
        </span>
      </div>

      {stale && (
        <p className="mt-3 text-xs text-[var(--destructive)]" data-testid="stale-economics-dossier">
          Alte Gebotsversion: Dossier neu aufbauen. Gespeicherte Vorschläge sind nicht anwendbar.
        </p>
      )}

      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-[var(--muted-foreground)]">Bestätigter Deckungsbeitrag</dt>
          <dd data-testid="confirmed-margin">
            {economics.confirmed_margin_net === null
              ? 'nicht bestätigt'
              : eur(economics.confirmed_margin_net)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--muted-foreground)]">Aktuelles Datenalter</dt>
          <dd data-testid="data-age">
            {formatAge(computeCurrentAgeSeconds(economics.checked_at, economics.data_age_seconds, nowMs))}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--muted-foreground)]">Datenalter (bei Erstellung)</dt>
          <dd data-testid="data-age-creation">{formatAge(economics.data_age_seconds)}</dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--muted-foreground)]">Gültig bis</dt>
          <dd data-testid="expires-at">{economics.expires_at ?? 'unbekannt'}</dd>
        </div>
      </dl>

      {!provider.enabled && (
        <p className="mt-3 text-xs text-[var(--destructive)]" data-testid="provider-disabled">
          Die Preisquelle ist nicht konfiguriert — es werden keine Gebote abgeleitet.
        </p>
      )}
      {provider.enabled && provider.error_code !== null && (
        <p className="mt-3 text-xs text-[var(--destructive)]" data-testid="provider-error">
          Die Preisquelle war nicht verwertbar (Grund: {safeOperatorText(provider.error_code)}).
        </p>
      )}
      <p className="mt-2 text-xs text-[var(--muted-foreground)]">
        Angefragt {provider.requested_skus} SKUs · beantwortet {provider.returned_rows} · bestätigt{' '}
        {provider.confirmed_rows}
      </p>
      <p className="mt-2 text-xs">Coverage: {dossier.coverage.mappedChildren} zugeordnete Childs · {dossier.coverage.campaignsInWave} Kampagnen in der Welle</p>
      <p className="mt-1 text-xs">Datenstand: {economics.expires_at && Date.parse(economics.expires_at) <= nowMs ? 'veraltet' : economics.checked_at ? 'bekannt' : 'unbekannt'}</p>
      <ReasonList title="Fehlende Konfiguration" reasons={provider.disabled_reasons} config />
      <ReasonList title="Sperrgründe" reasons={economics.block_reasons} />
    </Card>
  )
}

function QueryTable({ dossier }: { dossier: WaveDossier }) {
  if (dossier.queries.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-[var(--muted-foreground)]">
          Dieses Dossier enthält keine reifen Suchbegriffe.
        </p>
      </Card>
    )
  }
  const stale = dossier.economics?.economics_mode === 'shadow_v1'
    && dossier.economics.version !== WAVE_ECONOMICS_CURRENT_VERSION
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm">
        <caption className="px-4 py-2 text-left text-xs text-[var(--muted-foreground)]">
          Verbindlich: bestätigter Margen-Cap und CPC-Regel. Sellerboard-AOV-Cap nur optionaler Vergleich; er begrenzt das Zielgebot nicht.
        </caption>
        <thead>
          <tr className="border-b border-[var(--border)] text-left text-xs text-[var(--muted-foreground)]">
            <th scope="col" className="px-4 py-2">Suchbegriff</th>
            <th scope="col" className="px-4 py-2">Kampagne</th>
            <th scope="col" className="px-4 py-2">Margen-Cap €/Klick</th>
            <th scope="col" className="px-4 py-2">AOV-Vergleich €/Klick (optional)</th>
            <th scope="col" className="px-4 py-2">Abweichung</th>
            <th scope="col" className="px-4 py-2">Zielgebot</th>
            <th scope="col" className="px-4 py-2">Zustand</th>
          </tr>
        </thead>
        <tbody>
          {dossier.queries.map((query) => {
            const economics = query.economics
            const cell = deviationCell(economics)
            return (
              <tr key={`${query.sourceCampaignId}-${query.term}`} className="border-b border-[var(--border)]">
                <td className="px-4 py-2">{query.term}</td>
                <td className="px-4 py-2">{query.sourceCampaignId}</td>
                <td className="px-4 py-2" data-testid={`margen-cap-${query.term}`}>
                  {economics?.stahlia_margin_cap == null ? '—' : eur(economics.stahlia_margin_cap)}
                </td>
                <td className="px-4 py-2" data-testid={`aov-cap-${query.term}`}>
                  {economics?.legacy_aov_cap == null ? 'fehlt' : economics.legacy_aov_cap === 0 ? '0,00 € (kein Vergleich)' : eur(economics.legacy_aov_cap)}
                </td>
                <td
                  className={cell.highlight ? 'px-4 py-2 font-bold text-[var(--destructive)]' : 'px-4 py-2'}
                  data-testid={`abweichung-${query.term}`}
                  data-highlight={cell.highlight ? 'true' : 'false'}
                >
                  {cell.text}
                </td>
                <td className="px-4 py-2" data-testid={`zielgebot-${query.term}`}>
                  {stale ? 'veraltet — neu aufbauen' : query.startBid === null ? 'gesperrt' : eur(query.startBid)}
                </td>
                <td className="px-4 py-2 text-xs text-[var(--muted-foreground)]" data-testid={`gruende-${query.term}`}>
                  {stale ? 'alte Gebotsversion' : economics?.blocked === false
                    ? 'anwendbar'
                    : (economics?.block_reasons.map(safeOperatorText).join(', ') || 'gesperrt')}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

function ProfileAndWavePicker({ currentWaveId, profileId, onProfileChange }: { currentWaveId: number | null; profileId: number | null; onProfileChange: (id: number | null) => void }) {
  const navigate = useNavigate()
  const profilesQuery = useProfiles()
  const [offset, setOffset] = useState(0)
  const wavesQuery = useWaves(profileId, offset)
  const profiles = (profilesQuery.data ?? []).filter((profile) => Number.isSafeInteger(profile.id) && profile.id > 0)
  const list = wavesQuery.data
  return (
    <Card className="space-y-3 p-4" data-testid="wave-picker">
      <h2 className="text-sm font-semibold">Profil und Welle auswählen</h2>
      <label className="block text-xs" htmlFor="profile-select">Profil</label>
      {profilesQuery.isPending && <p>Profile werden geladen …</p>}
      {profilesQuery.isError && <p role="alert">Profile konnten nicht geladen werden.</p>}
      {profilesQuery.isSuccess && profiles.length === 0 && <p>Keine Profile vorhanden.</p>}
      <select id="profile-select" value={profileId ?? ''} onChange={(event) => {
        const next = parseWaveId(event.target.value)
        onProfileChange(next)
        setOffset(0)
        if (currentWaveId !== null) navigate(next === null ? '/wave-economics?choose=1' : `/wave-economics?profile=${next}`)
      }} className="rounded-md border border-[var(--border)] bg-[var(--background)] p-2 text-sm">
        <option value="">Profil wählen</option>
        {profiles.map((profile) => <option key={profile.id} value={profile.id}>{safeOperatorText(profile.name || `Profil ${profile.id}`)}</option>)}
      </select>
      {profileId !== null && (
        <div>
          {wavesQuery.isPending && <p>Wellen werden geladen …</p>}
          {wavesQuery.isError && <p role="alert">Wellen konnten nicht geladen werden.</p>}
          {wavesQuery.isSuccess && !Array.isArray(list?.items) && <p>Wellenliste unbekannt. Bitte aktualisieren.</p>}
          {wavesQuery.isSuccess && list && Array.isArray(list.items) && list.items.length === 0 && <p>Keine Wellen auf dieser Seite.</p>}
          {wavesQuery.isSuccess && list && Array.isArray(list.items) && (
            <>
              <ul className="space-y-1 text-sm">
                {list.items.filter((wave) => Number.isSafeInteger(wave.id) && wave.id > 0 && wave.profileId === profileId).map((wave) =>
                  <li key={wave.id}><button type="button" className="underline" onClick={() => navigate(`/wave-economics/${wave.id}?profile=${profileId}`)}>
                    Welle {wave.id} · {wave.lagerCode} · {wave.status}
                  </button></li>)}
              </ul>
              <div className="mt-2 flex gap-2 text-xs">
                <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Zurück</button>
                <span>{offset + 1}–{Math.min(offset + 25, list.total)} von {list.total}</span>
                <button type="button" disabled={offset + 25 >= list.total} onClick={() => setOffset(offset + 25)}>Weitere Wellen</button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

function LiveWaveEconomics() {
  const params = useParams()
  const location = useLocation()
  const waveId = parseWaveId(params['id'])
  const dossierQuery = useWaveDossier(waveId)
  const proposalsQuery = useWaveProposals(waveId)
  const [loadReadiness, setLoadReadiness] = useState(false)
  const readinessQuery = useWaveReadiness(loadReadiness ? waveId : null)
  const [showPicker, setShowPicker] = useState(() => new URLSearchParams(location.search).has('profile') || new URLSearchParams(location.search).has('choose'))
  const [profileId, setProfileId] = useState<number | null>(() => parseWaveId(new URLSearchParams(location.search).get('profile') ?? undefined))
  useEffect(() => {
    const query = new URLSearchParams(location.search)
    setProfileId(parseWaveId(query.get('profile') ?? undefined))
    if (query.has('profile') || query.has('choose')) setShowPicker(true)
  }, [location.search])

  const head = (
    <SectionHead
      title="Wellen-Economics (Shadow-v1)"
      subtitle="Beobachtung, kein Eingriff: diese Ansicht schreibt nichts und schaltet nichts scharf."
    />
  )

  if (waveId === null) {
    return (
      <div className="space-y-4">
        {head}
        <WaveIdForm initial={params['id'] ?? ''} profileId={profileId} />
        <button type="button" onClick={() => setShowPicker(true)}>Profile und Wellen laden</button>
        {showPicker && <ProfileAndWavePicker currentWaveId={null} profileId={profileId} onProfileChange={setProfileId} />}
      </div>
    )
  }

  const error = dossierQuery.error
  const notFound = error instanceof ApiError && error.status === 404
  const readinessChecks = readinessQuery.data?.checks

  return (
    <div className="space-y-4">
      {head}
      <WaveIdForm initial={String(waveId)} profileId={profileId} />
      <button type="button" onClick={() => setShowPicker(true)}>Profile und Wellen laden</button>
      {showPicker && <ProfileAndWavePicker currentWaveId={waveId} profileId={profileId} onProfileChange={(id) => { setProfileId(id); setShowPicker(true) }} />}

      <Card className="p-4" data-testid="readiness">
        <h2 className="text-sm font-semibold">Readiness-Checkliste</h2>
        {!loadReadiness && <button type="button" onClick={() => setLoadReadiness(true)}>Readiness prüfen</button>}
        {loadReadiness && <button type="button" disabled={readinessQuery.isFetching || dossierQuery.isFetching}
          onClick={() => { void readinessQuery.refetch(); void dossierQuery.refetch() }}>Daten aktualisieren</button>}
        {loadReadiness && readinessQuery.isPending && <p>Readiness wird geladen …</p>}
        {readinessQuery.isError && <p role="alert">{readinessQuery.error instanceof ApiError && readinessQuery.error.status === 409
          ? '409: Readiness oder Scope hat sich geändert. Bitte aktualisieren.'
          : 'Readiness konnte nicht geladen werden. Bitte aktualisieren.'}</p>}
        {readinessQuery.isSuccess && (!Array.isArray(readinessChecks) || readinessChecks.length === 0) && <p>Readiness unbekannt: keine Prüfungen vorhanden.</p>}
        {readinessQuery.isSuccess && Array.isArray(readinessChecks) && readinessChecks.length > 0 && (
          <>
            <ul className="mt-2 list-disc pl-5 text-sm">
              {readinessChecks.map((check) => <li key={`${check.category}-${check.key}`}>
                {safeOperatorText(check.key)}: {check.status === 'pass' ? 'erfüllt' : check.status === 'blocked' ? 'blockiert' : 'unbekannt'}
                {check.status !== 'pass' && <> · Nächster Schritt: {safeOperatorText(check.nextAction)}</>}
              </li>)}
            </ul>
            <p className="mt-2 text-xs">Vorschau: {readinessQuery.data?.previewReady ? 'bereit' : 'fachlich blockiert'} · Live: nicht freigegeben</p>
          </>
        )}
      </Card>

      {dossierQuery.isLoading && (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          Dossier wird geladen …
        </p>
      )}

      {!dossierQuery.isLoading && notFound && (
        <Card className="p-4" data-testid="no-dossier">
          <p className="text-sm">
            Für Welle {waveId} ist noch kein Dossier gespeichert. Der Aufbau ist ein eigener,
            berechtigungspflichtiger Serverschritt.
          </p>
        </Card>
      )}

      {!dossierQuery.isLoading && error !== null && !notFound && (
        <Card className="p-4" data-testid="dossier-error">
          <p className="text-sm text-[var(--destructive)]">
            {error instanceof ApiError && error.status === 409
              ? '409: Dossier oder Scope hat sich geändert. Bitte aktualisieren.'
              : `Das Dossier konnte nicht geladen werden: ${error instanceof Error ? safeOperatorText(error.message) : 'unbekannter Fehler'}`}
          </p>
        </Card>
      )}

      {!dossierQuery.isLoading && dossierQuery.data?.dossier && (
        <>
          <EconomicsHead dossier={dossierQuery.data.dossier} />
          <QueryTable dossier={dossierQuery.data.dossier} />
          <Card className="p-4">
            <p className="text-xs text-[var(--muted-foreground)]" data-testid="proposal-summary">
              {proposalsQuery.data
                ? `${proposalsQuery.data.proposals.filter((p) => p.applicable).length} von `
                  + `${proposalsQuery.data.proposals.length} gespeicherten Vorschlägen sind noch an dieses `
                  + 'Dossier gebunden; alle übrigen sind mit wirksamem Gebot 0,00 € ausgewiesen.'
                : 'Vorschläge werden geladen …'}
            </p>
          </Card>
        </>
      )}
      <RolloutEditor key={`${waveId}-${profileId ?? ''}`} waveId={waveId}
        readiness={!readinessQuery.isSuccess || readinessQuery.isFetching || !dossierQuery.isSuccess || dossierQuery.isFetching ||
          (profileId !== null && readinessQuery.data?.profileId !== profileId) ? undefined : readinessQuery.data}
        dossier={dossierQuery.data?.dossier} />
    </div>
  )
}

export default function WaveEconomics() {
  const location = useLocation()
  const demo = import.meta.env.DEV && new URLSearchParams(location.search).get('demo') === '1'
  return demo ? <DemoWaveEconomics /> : <LiveWaveEconomics />
}
